'use client';
import { useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import './styles.css';
import Button from '@mui/material/Button';
import { PiBuildingApartmentBold, PiClockClockwiseBold, PiChartLineUpBold } from 'react-icons/pi';
import { RiUserSettingsLine } from 'react-icons/ri';
import { FiLogOut } from 'react-icons/fi';
import { MdOutlineReviews } from 'react-icons/md';
import { CgFileDocument } from 'react-icons/cg';
import { IoIosArrowUp, IoIosArrowDown } from 'react-icons/io';
import strings from 'content/strings';
import { useMobileMenu } from 'hooks/useMobileMenu';
import { Box, Typography } from '@mui/material';
import Toast from 'common-components/Toast';
import { ToastState } from 'types/common';
import { AuthAPIs } from 'lib/apis/authApis';
import { useAuthStore } from 'store/useAuthStore';
import { ModuleKey } from 'types/adminTypes';
import { getAllowedModules } from 'lib/auth/permissionsUtils';
import { MODULE_KEYS } from 'lib/constants';
import { useFilterOptionsStore } from 'store/useFilterOptionsStore';
import ConfirmationDialog from 'common-components/ConfirmationDialog/ConfirmationDialog';

const navbarStrings = strings.navbar;

export const navbarOptions = [
  {
    label: navbarStrings.eventAnalytics,
    key: MODULE_KEYS.analytics,
    icon: <PiChartLineUpBold size={22} />,
    subItems: [
      { label: navbarStrings.overview, key: 'overview', href: '/event-analytics/overview' },
      {
        label: navbarStrings.pagesAndScreens,
        key: MODULE_KEYS.analytics_pages_screens,
        href: '/event-analytics/pages-and-screens',
      },
      {
        label: navbarStrings.userEngagements,
        key: MODULE_KEYS.analytics_user_engagements,
        href: '/event-analytics/events',
      },
      {
        label: navbarStrings.user,
        key: 'user',
        href: '/event-analytics/user',
      },
    ],
  },
  {
    label: navbarStrings.projectUpdates,
    key: MODULE_KEYS.project_updates,
    href: '/project-updates',
    icon: <PiClockClockwiseBold size={22} />,
  },
  {
    label: navbarStrings.helpAndSupport,
    key: MODULE_KEYS.help_and_support,
    href: '/help-and-support',
    icon: <MdOutlineReviews size={22} />,
  },
  {
    label: navbarStrings.content,
    key: MODULE_KEYS.content,
    href: '/content',
    icon: <CgFileDocument size={22} />,
  },
  {
    label: navbarStrings.projectDetails,
    key: MODULE_KEYS.project_details,
    icon: <PiBuildingApartmentBold size={22} />,
    subItems: [
      {
        label: navbarStrings.projects,
        key: MODULE_KEYS.project_details_projects,
        href: '/project-details/projects',
      },
      {
        label: navbarStrings.clusers,
        key: MODULE_KEYS.project_details_clusters,
        href: '/project-details/clusters',
      },
      {
        label: navbarStrings.towers,
        key: MODULE_KEYS.project_details_towers,
        href: '/project-details/towers',
      },
      {
        label: navbarStrings.units,
        key: MODULE_KEYS.project_details_units,
        href: '/project-details/units',
      },
    ],
  },
  {
    label: navbarStrings.admin,
    key: MODULE_KEYS.admin,
    href: '/admin',
    icon: <RiUserSettingsLine size={22} />,
  },
];

const getNavKeyForPath = (path: string) => {
  const activeOption = navbarOptions.find((option) => {
    if (option.href && (path === option.href || path.startsWith(option.href + '/'))) return true;

    return option.subItems?.some((sub) => path === sub.href || path.startsWith(sub.href + '/'));
  });

  const activeSubOption = activeOption?.subItems?.find(
    (sub) => path === sub.href || path.startsWith(sub.href + '/')
  );

  return activeSubOption?.key ?? activeOption?.key;
};

const Navbar = () => {
  const [activeParentMenu, setActiveParentMenu] = useState<string>('');
  const { user } = useAuthStore();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'error',
  });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const { logout } = AuthAPIs;
  const { clearSession } = useAuthStore();

  const pathname = usePathname() || '/';
  const allowedModules = useMemo(() => getAllowedModules(user), [user]);

  const isOptionActive = (href: string) => pathname === href || pathname.startsWith(href + '/');

  const handleToggle = (key: string) => {
    setActiveParentMenu((prev) => (prev === key ? '' : key));
  };

  const handleNavClick = (targetNavKey?: string) => {
    const currentNavKey = getNavKeyForPath(pathname);

    if (targetNavKey && currentNavKey && targetNavKey !== currentNavKey) {
      useFilterOptionsStore.getState().resetTabs();
    }

    setIsMobileMenuOpen(false);
  };

  const handleLogout = () =>
    logout().finally(() => {
      setTimeout(() => {
        clearSession();
      }, 3000);
    });

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    handleLogout();
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const onCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  if (pathname === '/' || pathname.startsWith('/sign-in')) return null;

  return (
    <div className={`navbar-container ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
      <Link
        href={'/event-analytics/overview'}
        className="navbar-spl-logo"
        onClick={() => handleNavClick('overview')}
      >
        <img src="/icons/brand-logo.svg" alt="Logo" style={{ width: 120 }} />
      </Link>

      <nav>
        {navbarOptions.map((option) => {
          if (!allowedModules || !allowedModules.includes(option.key as ModuleKey)) return null;

          const hasSubItems = !!option.subItems?.length;

          // Parent active ONLY if child route matches
          const isParentActive =
            hasSubItems && option.subItems?.some((sub) => isOptionActive(sub.href));

          return (
            <Box key={option.key}>
              {hasSubItems ? (
                <>
                  <Box
                    className={`parent-item ${isParentActive ? 'active' : ''}`}
                    onClick={() => handleToggle(option.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: '24px', opacity: '100%' }}>
                      {option.icon}
                      <Typography variant="h6" sx={{ fontSize: 16 }}>
                        {option.label}
                      </Typography>
                    </Box>

                    {activeParentMenu === option.key ? (
                      <IoIosArrowUp size={18} />
                    ) : (
                      <IoIosArrowDown size={18} />
                    )}
                  </Box>

                  {activeParentMenu === option.key && (
                    <Box className="submenu-container">
                      {option.subItems?.map((sub) => {
                        const isActive = isOptionActive(sub.href);

                        return (
                          <Link
                            key={sub.key}
                            href={sub.href}
                            className={`menu-item ${isActive ? 'active' : ''}`}
                            onClick={() => handleNavClick(sub.key)}
                          >
                            <Typography variant="h6" sx={{ fontSize: 16 }}>
                              {sub.label}
                            </Typography>
                          </Link>
                        );
                      })}
                    </Box>
                  )}
                </>
              ) : (
                <div className="menu-item-container">
                  <Link
                    href={option.href!}
                    className={`menu-item ${isOptionActive(option.href!) ? 'active' : ''}`}
                    onClick={() => handleNavClick(option.key)}
                  >
                    {option.icon}
                    <Typography variant="h6" sx={{ fontSize: 16 }}>
                      {option.label}
                    </Typography>
                  </Link>
                </div>
              )}
            </Box>
          );
        })}
      </nav>

      <div className="logout-container">
        <Button variant="text" color="error" startIcon={<FiLogOut />} onClick={handleLogoutClick}>
          {navbarStrings.logout}
        </Button>
      </div>
      <ConfirmationDialog
        open={showLogoutConfirm}
        titleColor={'#D32F2F'}
        icon={<FiLogOut size={30} color="#D32F2F" />}
        title={strings.auth.logoutDialogTitle}
        message={strings.auth.logoutDialogMessage}
        confirmButtonTitle="Logout"
        onConfirm={handleConfirmLogout}
        onClose={handleCancelLogout}
        buttonType="double"
        dismissable={false}
      />
      <Toast
        isOpen={toast.open}
        onClose={onCloseToast}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
};

export default Navbar;
